// Lesson navigation + per-student lesson progress.
// All reads/writes go through Supabase under RLS (browser, anon key).

import { supabase } from "./supabase";
import type { LessonRow, ModuleRow, LessonProgressRow } from "./types";

export type LessonWithModule = LessonRow & { module: ModuleRow | null };

/** Fetch a single lesson plus its parent module. */
export async function fetchLesson(
  lessonId: string,
): Promise<LessonWithModule | null> {
  const { data: lesson, error } = await supabase
    .from("lessons")
    .select("id, module_id, type, title, order, body_ref, required_for_completion")
    .eq("id", lessonId)
    .maybeSingle();
  if (error || !lesson) return null;

  const { data: mod } = await supabase
    .from("modules")
    .select("id, cohort_id, session_no, block_no, title, order, objectives")
    .eq("id", (lesson as LessonRow).module_id)
    .maybeSingle();

  return { ...(lesson as LessonRow), module: (mod as ModuleRow) ?? null };
}

/** All lessons in a module, ordered — used for prev/next navigation. */
export async function fetchModuleLessons(moduleId: string): Promise<LessonRow[]> {
  const { data } = await supabase
    .from("lessons")
    .select("id, module_id, type, title, order, body_ref, required_for_completion")
    .eq("module_id", moduleId)
    .order("order", { ascending: true });
  return (data as LessonRow[] | null) ?? [];
}

/** Lesson-progress rows for a student across many lessons (dashboard). */
export async function fetchProgressForLessons(
  studentId: string,
  lessonIds: string[],
): Promise<Record<string, LessonProgressRow>> {
  if (!studentId || lessonIds.length === 0) return {};
  const { data } = await supabase
    .from("lesson_progress")
    .select("id, student_id, lesson_id, completed_at")
    .eq("student_id", studentId)
    .in("lesson_id", lessonIds);
  const map: Record<string, LessonProgressRow> = {};
  for (const row of (data as LessonProgressRow[] | null) ?? []) {
    map[row.lesson_id] = row;
  }
  return map;
}

/** Single lesson's progress for a student. */
export async function fetchLessonProgress(
  studentId: string,
  lessonId: string,
): Promise<LessonProgressRow | null> {
  if (!studentId) return null;
  const { data } = await supabase
    .from("lesson_progress")
    .select("id, student_id, lesson_id, completed_at")
    .eq("student_id", studentId)
    .eq("lesson_id", lessonId)
    .maybeSingle();
  return (data as LessonProgressRow | null) ?? null;
}

/** Mark a lesson complete (idempotent upsert on student_id + lesson_id). */
export async function markLessonComplete(
  studentId: string,
  lessonId: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from("lesson_progress").upsert(
    {
      student_id: studentId,
      lesson_id: lessonId,
      completed_at: new Date().toISOString(),
    },
    { onConflict: "student_id,lesson_id" },
  );
  return { error: error?.message ?? null };
}

/** Clear a lesson's completion (un-mark). */
export async function unmarkLessonComplete(
  studentId: string,
  lessonId: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from("lesson_progress")
    .delete()
    .eq("student_id", studentId)
    .eq("lesson_id", lessonId);
  return { error: error?.message ?? null };
}
